import {NfHotelComponentBase} from "../NfHotelComponentBase";
import {computed, inject, reactive, type Ref, watch} from "vue";
import {provideIntl} from "vue-intl";
import {getTranslation, intl, translatedString} from "@/utilities/translations";
import {NfServiceWorkerCommunication} from "@/services/NfServiceWorkerCommunication";
import {ComponentTypes} from "@/serviceWorker/workerComponents/ComponentTypes";
import {showPromiseError} from "@/utilities/nonDomUtils";
import {onMainComponentLoaded} from "@/utilities/domUtils";
import {NfMessageType} from "@/serviceWorker/workerComponents/NfMessage";
import {SetReservationSearchData} from "@/serviceWorker/workerComponents/MessageTypes/SetReservationSearchData";
import type {ModalData} from "@/frontendClasses/ModalData";
import type {FindFirstPeriodMode} from "@/frontendClasses/FindFirstPeriodMode";
import type {OfferModel} from "@/serviceWorker/workerComponents/Models/OfferModel";
import type {RoomStandardDescriptionModel} from "@/serviceWorker/workerComponents/Models/RoomStandardDescriptionModel";
import type {OfferAvailability} from "@/serviceWorker/workerComponents/Models/OfferAvailability";
import {ReservationSearchData} from "@/serviceWorker/workerComponents/Models/ReservationSearchData";
import type {ShopChosenModel} from "@/serviceWorker/workerComponents/Models/ShopChosenModel";
import type {CalculationModel} from "@/serviceWorker/workerComponents/Models/CalculationModel";
import type {CustomerModel} from "@/serviceWorker/workerComponents/Models/CustomerModel";
import type {CompanyModel} from "@/serviceWorker/workerComponents/Models/CompanyModel";
import type {PaymentMethods} from "@/serviceWorker/workerComponents/Models/PaymentMethods";
import type {BookingParameters} from "@/services/NfBookingService";
import {bookGroupReservation, bookReservation, GroupBookingParameters} from "@/services/NfBookingService";
import {
    GetReservationCalculationResult
} from "@/serviceWorker/workerComponents/MessageTypes/GetReservationCalculationResult";
import {SetShopChosen} from "@/serviceWorker/workerComponents/MessageTypes/SetShopChosen";
import {SetCustomerData} from "@/serviceWorker/workerComponents/MessageTypes/SetCustomerData";
import {SetOfferChosen} from "@/serviceWorker/workerComponents/MessageTypes/SetOfferChosen";
import {SetRoomStandardChosen} from "@/serviceWorker/workerComponents/MessageTypes/SetRoomStandardChosen";
import {SetBookingChosen} from "@/serviceWorker/workerComponents/MessageTypes/SetBookingChosen";
import {AvailabilityData} from "@/serviceWorker/workerComponents/MessageTypes/AvailabilityData";
import {SetGroupReservationBasket} from "@/serviceWorker/workerComponents/MessageTypes/SetGroupReservationBasket";
import type {GroupReservationModel} from "@/serviceWorker/workerComponents/Models/GroupReservationModel";
import {NfGroupReservationService} from "@/services/NfGroupReservationService";
import {setWidgetId} from "@/utilities/templateLoader";
import type {ModalInstanceData} from "@/frontendClasses";
import type {PageData} from "@/frontendClasses/PageData";
import {NavigationPageName, NavigationPages} from "@/frontendClasses/NavigationPages";
import {NavigateRequest} from "@/serviceWorker/workerComponents/MessageTypes/NavigateRequest";
import {NfConfigService} from "@/services/NfConfigService";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";
import type {
    HotelTranslatedConfigurationModel
} from "@/serviceWorker/workerComponents/Models/HotelTranslatedConfigurationModel";
import type {EquipmentModel} from "@/serviceWorker/workerComponents/Models/EquipmentModel";
import {
    GetEquipmentDescriptionsResult
} from "@/serviceWorker/workerComponents/MessageTypes/GetEquipmentDescriptionsResult";
import {SetBookingResult} from "@/serviceWorker/workerComponents/MessageTypes/SetBookingResult";
import {CheckBookingPossibleResult} from "@/serviceWorker/workerComponents/MessageTypes/CheckBookingPossibleResult";
import {CheckBookingPossible} from "@/serviceWorker/workerComponents/MessageTypes/CheckBookingPossible";
import {AnalyticsService} from "@/services/NfAnalyticsService";
import type {GuestModel} from "@/serviceWorker/workerComponents/Models/GuestModel";
import type {PaymentData} from "@/serviceWorker/workerComponents/Models/PaymentData";
import {SetPaymentData} from "@/serviceWorker/workerComponents/MessageTypes/SetPaymentData";
import {SetGroupBookingResult} from "@/serviceWorker/workerComponents/MessageTypes/SetGroupBookingResult";
import type {GroupBookingResult} from "@/frontendClasses/GroupBookingResult";
import type {BookingResult} from "@/frontendClasses/BookingResult";
import type {BookingCustomer} from "@/frontendClasses/BookingCustomer";
import type {SingleReservationInGroup} from "@/frontendClasses/SingleReservationInGroup";
import type {InstanceConfiguration} from "@/frontendClasses/InstanceConfiguration";
import {distinctUntilChanged, type Subscription} from "rxjs";
import {ReloadBookingEngine} from "@/serviceWorker/workerComponents/MessageTypes/ReloadBookingEngine";
import {WebDeskKioskStore} from "@/store/WebDeskKioskStore";
import {NfHotelShopStore} from "@/store/NfHotelShopStore";
import {NfCustomerStore} from "@/store/NfCustomerStore";
import {cloneDeep, isEqual} from "lodash-es";
import {NfSearchService} from "@/services/NfSearchService";
import type {PacketDescriptionModel} from "@/serviceWorker/workerComponents/Models/PacketDescriptionModel";
import {NfFavoritesService} from "@/services/NfFavoritesService";


type ConstructorTypes = StringConstructor | NumberConstructor | BooleanConstructor | ArrayConstructor;
type InstanceOfConstructor<T> =  T extends StringConstructor ? string : (T extends NumberConstructor ? number: (T extends BooleanConstructor ? boolean: (T extends ArrayConstructor ? any[]: never)));
declare interface PropOptions<T extends ConstructorTypes> {
    type?: T;
    required?: boolean;
    default?: InstanceOfConstructor<T> | null | undefined;
    validator?(value: T): boolean;
}
export declare type ComponentObjectPropsOptions =  Record<string, PropOptions<ConstructorTypes> | ConstructorTypes>;


/**
 * Klasa bazowa dla komponentów głównych booking engine - czyli takich które mogą być wstawiane jako Vue app
 */
export abstract class NfHotelMainComponentBase extends NfHotelComponentBase {
    public isMainComponent = true;
    protected $mainPropsLoaded = false;
    public componentType = ComponentTypes.MainModal;
    public requestedGlobalMessageTypes: NfMessageType[] = [NfMessageType.SetBookingChosen, NfMessageType.SetRoomStandardChosen,
        NfMessageType.SetOfferChosen, NfMessageType.SetCustomerData, NfMessageType.SetShopChosen,
        NfMessageType.GetReservationCalculationResult, NfMessageType.SetReservationSearchData, NfMessageType.AvailabilityData,
        NfMessageType.SetGroupReservationBasket, NfMessageType.GetBookingEngineConfigurationResult, NfMessageType.GetTranslationsResult,
        NfMessageType.GetStylesResult, NfMessageType.GetHotelTranslatedConfigurationResult, NfMessageType.ReloadBookingEngine,
        NfMessageType.AvailabilitySearchStarted];

    declare private analytics: AnalyticsService;
    declare private searchService: NfSearchService;

    setup(props: ComponentObjectPropsOptions, context: any) {
        provideIntl(intl);
        if(props.widgetId) {
            // @ts-ignore
            setWidgetId(props.widgetId);
        } else if(inject('widgetId')) {
            // @ts-ignore
            setWidgetId(inject('widgetId'));
        }
    }


    /**
     * Wynik tej funkcji należy podać do createApp() żeby Vue przetrawiło ten komponent
     */
    getAppFunction() {
        const result = function () {
        };
        result.__vccOpts = this;
        return result;
    }
    beforeCreate() {
        super.beforeCreate();
        // @ts-ignore
        this.$mainProps = reactive({});
        for(let prop in this.$props as any) {
            // @ts-ignore
            this.$mainProps[prop] = this.$props[prop];
        }
    }
    /**
     * Zmienna $el nie zainicjalizowana
     * This dostępne
     */
    created() {
        //@ts-ignore
        this.configService = reactive(null);
    }
    /**
     * Załadowane atrybuty głównego komponentu, reaktywnie, doładują się w etapie mounted
     */
    declare public $mainProps:  {
        [K in keyof this['props']]:   Exclude<this['props'][K] extends ConstructorTypes ? InstanceOfConstructor<this['props'][K]> : ( this['props'][K] extends PropOptions<ConstructorTypes> ? this['props'][K]['type'] : this['props'][K] ), ConstructorTypes>
    } & {widgetId: number, serviceInstanceId?: number, languageCode?: string,simplify?: boolean};


    props =  {
        widgetId: Number,
        serviceInstanceId: Number
    } as any;
    data() {
        return {
            ...super.data(),
            serviceInstanceId: null,
            widgetId: this.$mainProps?.widgetId as number | null | undefined,
            modals: [] as ModalInstanceData[],
            isSearchInProgress: false as boolean,
            currentPage: 'offers' as NavigationPageName,
            offerChosen: null as  OfferModel | null,
            packetChosen: null as  PacketDescriptionModel | null,
            standardChosen: null as  RoomStandardDescriptionModel | null,
            bookingChosen: null as OfferAvailability | null,
            currentPageData: null as PageData | null,
            reservationSearchData: new ReservationSearchData(),
            shopChosen: null as ShopChosenModel | null,
            calculation: null as CalculationModel | null,
            customerData: null as CustomerModel | null,
            invoiceData: undefined as CompanyModel | undefined,
            guestData: null as GuestModel[] | null,
            comment: undefined as string | undefined,
            paymentMethod: null as PaymentMethods | null,
            bookingResult: null as BookingResult | null,
            groupBookingResult: null as GroupBookingResult | null,
            paymentData: null as PaymentData | null,
            findFirstPeriod: null as FindFirstPeriodMode,
            groupReservationBasket: null as GroupReservationModel | null,
            config: null as BookingEngineConfigModel|null,
            translatedConfig: null as HotelTranslatedConfigurationModel|null,
            groupReservationService: null as NfGroupReservationService | null,
            configService: null as NfConfigService|null,
            favoritesService: null as NfFavoritesService|null,
            equipmentDescriptions: null as EquipmentModel[]|null,
            instanceConfiguration: null as InstanceConfiguration|null,
            simplify: null as Boolean|null,
        };
    }

    declare protected communication: NfServiceWorkerCommunication;
    /**
     * @inheritDoc
     * Tutaj ładowane są atrybuty głównego komponentu
     */
    mounted() {
        super.mounted();
        // @ts-ignore
        //region ładowanie atrybutów głównego komponentu
        for(let prop in this.$props) {
            const attributeName = prop.replace(/([a-z])([A-Z])/g,'$1-$2').toLowerCase();
            if(this.$el?.parentElement && this.$el.parentElement.hasAttribute(attributeName)) {
                const propValue = this.$el.parentElement.getAttribute(attributeName);
                // @ts-ignore
                const type =this.$options.props[prop]?.type || this.$options.props[prop];
                if(type) {
                    if(type == Array) {
                        // @ts-ignore
                        this.$mainProps[prop] = (propValue as string).split(',');
                    } else {
                        // @ts-ignore
                        this.$mainProps[prop] = type(propValue);
                    }
                }
            } else {
                // @ts-ignore
                if(this.$options.props[prop] && typeof(this.$options.props[prop].default) !== 'undefined') {
                    // @ts-ignore
                    this.$mainProps[prop] = this.$options.props[prop].default;
                }
            }
        }
        this.$mainPropsLoaded = true;
        //endregion

        // Tutaj mamy wszystkie properki załadowane
        if(!this.$mainProps.serviceInstanceId) {
            this.communication = new NfServiceWorkerCommunication(this.$mainProps.widgetId as number, this.componentType, this.name,
                this.$options.requestedGlobalMessageTypes);
            this.communication.serviceInstancePromise.then(
                (serviceInstanceId) => {
                    //@ts-ignore
                    this.$data.serviceInstanceId = serviceInstanceId;
                    //@ts-ignore
                    this.$data.widgetId = this.$mainProps?.widgetId;
                    if(window.nfShowDebugMessages) {
                        console.debug("Nowa instancja booking engine: " + serviceInstanceId);
                    }
                    const event = new CustomEvent('nfHotelElement.InstanceReady', {
                        detail: {
                            serviceInstanceId: serviceInstanceId,
                            swCommunication: this.communication,
                            communicationClasses: {
                                SetReservationSearchData,
                                NavigateRequest
                            },
                            translate: getTranslation,
                            component: this
                        },
                        bubbles: true,
                        composed: true
                    });
                    this.$el?.dispatchEvent(event);
                }).catch(showPromiseError('While loading service instance promise'));
        } else {

            this.communication = new NfServiceWorkerCommunication(this.$mainProps.widgetId as number, this.componentType, this.name,
                this.$options.requestedGlobalMessageTypes, this.$mainProps.serviceInstanceId as number);

            //@ts-ignore
            this.$data.serviceInstanceId = this.$mainProps?.serviceInstanceId;
            //@ts-ignore
            this.$data.widgetId = this.$mainProps?.widgetId;
            console.debug("Dołączam do istniejącej instancji booking engine: "+this.$mainProps?.serviceInstanceId);
            const event = new CustomEvent('nfHotelElement.InstanceReady', {
                detail: {
                    serviceInstanceId: this.$mainProps?.serviceInstanceId,
                    swCommunication: this.communication,
                    communicationClasses: {
                        SetReservationSearchData,
                        NavigateRequest
                    },
                    component: this
                },
                bubbles: true
            });
            this.$el?.dispatchEvent(event);
        }

        this.$data.configService = new NfConfigService(this.communication,this.$mainProps.widgetId as number, this.$mainProps.languageCode);
        this.$data.configService.configurationPromise.then(config => this.$data.config = config);
        this.$data.configService.translatedConfigurationPromise.then(config => this.$data.translatedConfig = config);

        this.$data.favoritesService = new NfFavoritesService(this.$mainProps.widgetId);
        this.$data.favoritesService.initialize(this.communication);

        this.$options.onMainComponentReady.bind(this)();
        if(this.$el) {
            onMainComponentLoaded(this.$el);
        }

    }

    onMainComponentReady() {
        this.analytics = new AnalyticsService(this.$data.configService as NfConfigService, this.$mainProps.widgetId as number);
        this.searchService = new NfSearchService(this.communication);
        watch(this.searchService.getSearchInProgress(), (isSearchInProgress: boolean) => {this.$data.isSearchInProgress = isSearchInProgress;});

        (this.communication.getMessageObservable(NfMessageType.SetReservationSearchData).subscribe(message => {
            if(message instanceof SetReservationSearchData) {
                this.rootObject?.onSearchChanged(message.getReservationSearchData());
            }
        }));

        //region obsługa danych zbieranych z różnych stron

        (this.communication.getMessageObservable(NfMessageType.GetReservationCalculationResult).subscribe(message => {
            if(message instanceof GetReservationCalculationResult) {
                this.$data.calculation = message.getCalculation();
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetShopChosen).subscribe(message => {
            if(message instanceof SetShopChosen) {
                this.$data.shopChosen = message.getShopChosen();
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetCustomerData).subscribe(message => {
            if(message instanceof SetCustomerData) {
                this.$data.customerData = message.customerData;
                this.$data.invoiceData = message.invoiceData;
                this.$data.comment = message.comment;
                this.$data.paymentMethod = message.paymentMethod;
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetOfferChosen).subscribe(message => {
            if(message instanceof SetOfferChosen) {
                this.$data.offerChosen = message.offerChosen;
                this.$data.packetChosen = message.packetChosen;
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetRoomStandardChosen).subscribe(message => {
            if(message instanceof SetRoomStandardChosen) {
                this.$data.standardChosen = message.roomStandardChosen;
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetBookingChosen).subscribe(message => {
            if(message instanceof SetBookingChosen) {
                this.$data.bookingChosen = message.bookingChosen;
            }
        }),
        this.communication.getMessageObservable(NfMessageType.SetGroupReservationBasket).subscribe(message => {
            if(message instanceof SetGroupReservationBasket) {
                this.$data.groupReservationBasket = message.getGroupReservationModel();
            }
        }),
        this.communication.getMessageObservable(NfMessageType.GetEquipmentDescriptionsResult).subscribe(message => {
            if(message instanceof GetEquipmentDescriptionsResult) {
                this.$data.equipmentDescriptions = message.equipmentDescriptions;
            }
        }),
        this.communication.getMessageObservable(NfMessageType.AvailabilityData)
            //optymalizujemy by nie strzelać do komponentów ciągle tymi samymi danymi.
            .pipe(distinctUntilChanged((a,b) =>{
                if (a instanceof AvailabilityData && b instanceof AvailabilityData) {
                    return isEqual(a.availabilityData, b.availabilityData)
                }
                return false;
            }))
            .subscribe(message => {
            if(message instanceof AvailabilityData) {
                if(message.availabilityData.force_jump_to_period?.from) {
                    const newFrom = window.moment(message.availabilityData.force_jump_to_period?.from);
                    const newTo = window.moment(message.availabilityData.force_jump_to_period?.to);
                    if(!newFrom.isSame(this.$data.reservationSearchData.dateFrom)
                    || !newTo.isSame(this.$data.reservationSearchData.dateTo)) {
                        const searchData = cloneDeep(this.$data.reservationSearchData);
                        searchData.dateFrom = newFrom;
                        searchData.dateTo = newTo;
                        this.communication.sendMessage(new SetReservationSearchData(this.communication.getComponentId(), searchData, true));
                    }
                }

                const discount_used = message.availabilityData.discount_used;

                if (discount_used === "not_found") {
                    // klient aplikował kod, ale go nie znalazło
                    this.openModal({
                        data: {
                            title: getTranslation('Niepoprawny kod rabatowy'),
                            text: getTranslation('Wprowadzony kod rabatowy jest niepoprawny. Spróbuj ponownie.'),
                        },
                        templateName: 'errorWindow',
                        position: 'screenCenter'
                    });
                } else if (discount_used === "applied") {
                    // klient aplikował kod i został znaleziony
                    this.openModal({
                        data: {
                            title: getTranslation('Kod rabatowy zastosowany'),
                            text: getTranslation('Kod rabatowy został pomyślnie zastosowany.'),
                        },
                        templateName: 'errorWindow',
                        position: 'screenCenter'
                    });
                }
            }
        }));
        //ja pier.. tego się nie odzobaczy xD
        this.$data.groupReservationService = new NfGroupReservationService(
            <any>computed( () => this.$data.groupReservationBasket) as GroupReservationModel,
            <any>computed( () => this.$data.shopChosen) as ShopChosenModel,
            <any>computed( () => this.$data.reservationSearchData) as ReservationSearchData,
            <any>computed( () => this.$data.standardChosen) as RoomStandardDescriptionModel,
            <any>computed( () => this.$data.bookingChosen) as OfferAvailability,
            <any>computed( () => this.$data.offerChosen) as OfferModel,
            <any>computed( () => this.$data.calculation) as CalculationModel,
            <any>computed( () => this.$data.config) as Ref<BookingEngineConfigModel>,
            <any>computed(() => this.analytics as AnalyticsService),
            this.communication
        );
        (this.communication.getMessageObservable(NfMessageType.SetReservationSearchData).subscribe(message => {
            if(message instanceof SetReservationSearchData) {
                this.$data.groupReservationService?.onReservationSearchDataChanged(message.getReservationSearchData());
            }
        }));
        this.communication.getNextMessagePromise(NfMessageType.ReloadBookingEngine).then((message) => {
            if(message instanceof ReloadBookingEngine) {
                this.reload(message.keepServiceInstance);
            }
        });
        NfHotelShopStore.init(this.$mainProps.widgetId, this.$data.configService!.currentLanguage.id);
        //endregion
    }
    nextModalInstanceId = 1;
    /**
     * Otwiera modala z podaną zawartością
     * Funkcja dostępna wszystkim podkomponentom poprzez provide
     * Zwraca unikatowy id modala
     */
    openModal(modalData: ModalData) {
        if(!modalData.componentName && !modalData.templateName) {
            throw new Error('Modal musi mieć ustawiony componentName lub templateName');
        }
        const modalInstanceData:ModalInstanceData = {
            uniqueId: this.nextModalInstanceId++,
            ...modalData
        };
        this.$data.modals.push(modalInstanceData);
        return modalInstanceData.uniqueId;
    }
    destroy() {
        if(this.shadowRoot) {
            NfCustomerStore.reset();
            //@ts-ignore
            this.$.appContext.app.unmount();
            const event = new CustomEvent('nfHotel.kill', {bubbles: true});
            this.shadowRoot.dispatchEvent(event);
        }
    }
    reload(keepServiceInstanceId = true) {
        if(this.shadowRoot) {
            //@ts-ignore
            this.$.appContext.app.unmount();
            const eventDetail = {serviceInstanceId: undefined as number|undefined};
            if(keepServiceInstanceId) {
                eventDetail.serviceInstanceId = this.communication?.serviceInstanceId;
            }
            const event = new CustomEvent('nfHotel.reload', {bubbles: true, detail: eventDetail});
            this.shadowRoot.dispatchEvent(event);
        }
    }

    // @ts-ignore Używać $mainProps w tym rodzaju komponentów
    declare $props: null;
    /**
     * Zamyka modala na podstawie unikatowego id
     * @param uniqueId
     */
    closeModal(uniqueId: number) {
        for(let i = 0; i < this.$data.modals.length; i++) {
            const modalInstanceData:ModalInstanceData = this.$data.modals[i];
            if(modalInstanceData.uniqueId === uniqueId) {
                this.$data.modals.splice(i, 1);
                return;
            }
        }
    }
    closeAllModals() {
        for(const modalData of this.$data.modals) {
            this.closeModal(modalData.uniqueId);
        }
    }

    sendSingleReservationRequest() {
        const mainReservation = this.$data.groupReservationBasket?.reservations[0];
        if(!mainReservation) {
            throw new Error('No reservation in group reservation basket');
        }


        const bookingParameters: BookingParameters = {
            adult_count: mainReservation.reservationSearchData.adultCount as number,
            com_content: this.$data.comment,
            config_id: mainReservation.chosenOffer.id as number,
            id: mainReservation.chosenStandardDescription.roomstandard_id,
            date_to: mainReservation.reservationSearchData.dateTo?.format('YYYY-MM-DD') as string,
            date_from: mainReservation.reservationSearchData.dateFrom?.format('YYYY-MM-DD') as string,
            payment_method: this.$data.paymentMethod as PaymentMethods,
            customer: {
                c_email: this.$data.customerData?.c_email as string,
                c_firstname: this.$data.customerData?.c_firstname as string,
                c_lastname: this.$data.customerData?.c_lastname as string,
                c_phone: this.$data.customerData?.c_phone as string,
                language_id: this.$data.configService?.currentLanguage.id as number,
                c_newsletter: this.$data.customerData?.c_newsletter,
                c_newsletter_sharing: this.$data.customerData?.c_newsletter_sharing,
                c_newsletter_profiling: this.$data.customerData?.c_newsletter_profiling,
                c_newsletter_after_leave: this.$data.customerData?.c_newsletter_after_leave,
                c_street: this.$data.customerData?.c_street as string,
                c_city: this.$data.customerData?.c_city as string,
                c_postcode: this.$data.customerData?.c_postcode as string,
                c_document_nr: this.$data.customerData?.c_document_nr as string,
                c_signature: this.$data.customerData?.c_signature,
            },
            guests: mainReservation.guestData as GuestModel[],
            discount_code: mainReservation.reservationSearchData.discountCode,
            parent_location: window.location.hostname,
            children_ages: mainReservation.reservationSearchData.childrenAges,
            chance_id: this.$data.instanceConfiguration?.chanceId
        };
        if(this.$data.invoiceData) {
            bookingParameters.customer.company = this.$data.invoiceData;
        }

        if(mainReservation.chosenShop?.chosenItems) {
            bookingParameters.shop_items_chosen = {};
            for(const chosenItemId in mainReservation.chosenShop.chosenItems) {
                bookingParameters.shop_items_chosen[chosenItemId] = mainReservation.chosenShop.chosenItems[chosenItemId].count;
            }
        }
        const analyticsData = {
            standardName: `${mainReservation.chosenStandardDescription.rsl_name}_${mainReservation.chosenStandardDescription.roomstandard_id}`,
            fullPrice: mainReservation.calculation.prices.r_grand_total,
            shop: mainReservation.chosenShop,
        };

        this.analytics.googlePurchase(analyticsData)
        this.analytics.facebookPurchase(analyticsData);

        return bookReservation(this.$mainProps.widgetId as number, bookingParameters).then(bookingResult => {
            const paymentData:PaymentData = {
                payment_methods: bookingResult.payment_methods,
                payment_token: bookingResult.payment_token,
                installment_payment: bookingResult.installment_payment,
                installment_date: bookingResult.installment_date,
                total_payment: bookingResult.total_payment,
                bank_transfer_description: bookingResult.payment_data,
                installment_required: bookingResult.installment_required,
            };
            this.$data.paymentData = paymentData;
            this.communication.sendMessage(new SetPaymentData(this.communication.getComponentId(),
                paymentData));
            this.$data.bookingResult = bookingResult;
            this.communication.sendMessage(new SetBookingResult(this.communication.getComponentId(),
                bookingResult));
            return bookingResult;
        });
    }
    sendGroupReservationRequest() {
        //STODO: przekazać jakoś external_customer_id
        let reservations: SingleReservationInGroup[] = [];
        if(this.$data.groupReservationBasket?.reservations) {
            for (let reservation of this.$data.groupReservationBasket?.reservations) {
                const reservationRequest = {
                    children_ages: reservation.reservationSearchData.childrenAges,
                    shop_items_chosen: {},
                    adult_count: reservation.reservationSearchData.adultCount as number,
                    com_content: reservation.comment,
                    offer_id: reservation.chosenOffer.id as number,
                    room_standard_id: reservation.chosenStandardDescription.roomstandard_id,
                    date_to: reservation.reservationSearchData.dateTo?.format('YYYY-MM-DD') as string,
                    date_from: reservation.reservationSearchData.dateFrom?.format('YYYY-MM-DD') as string,
                    customer: reservation.customerData ? {
                        ...reservation.customerData,
                        language_id: this.$data.configService?.currentLanguage.id as number,
                        company: this.$data.invoiceData

                    } : {
                        ...this.$data.customerData,
                        language_id: this.$data.configService?.currentLanguage.id as number,
                        company: this.$data.invoiceData
                    } as BookingCustomer,
                    guests: reservation.guestData as GuestModel[],
                    discount_code: reservation.reservationSearchData.discountCode} as SingleReservationInGroup;
                if(reservation.chosenShop?.chosenItems) {
                    reservationRequest.shop_items_chosen = {};
                    for(const chosenItemId in reservation.chosenShop.chosenItems) {
                        reservationRequest.shop_items_chosen[chosenItemId] = reservation.chosenShop.chosenItems[chosenItemId];
                    }
                }
                reservations.push(reservationRequest);
                const analyticsData = {
                    standardName: `${reservation.chosenStandardDescription.rsl_name}_${reservation.chosenStandardDescription.roomstandard_id}`,
                    fullPrice: reservation.calculation.prices.r_grand_total,
                    shop: reservation.chosenShop,
                };

                this.analytics.googlePurchase(analyticsData);
                this.analytics.facebookPurchase(analyticsData);
            }
        }
        let groupParameters: GroupBookingParameters = {
            payment_method: this.$data.paymentMethod as PaymentMethods,
            parent_location: window.location.hostname,
            com_content: this.$data.comment,
            chance_id: this.$data.instanceConfiguration?.chanceId,
            customer: {
                ...this.$data.customerData,
                company: this.$data.invoiceData
            } as BookingCustomer,
            reservations
        };
        //STODO: potrzebuję external_customer_id
        return bookGroupReservation(this.$mainProps.widgetId as number, groupParameters).then(groupBookingResult => {
            if(groupBookingResult) {
                const paymentData: PaymentData = {
                    payment_methods: groupBookingResult.payment_methods,
                    payment_token: groupBookingResult.payment_token,
                    installment_payment: groupBookingResult.installment_payment,
                    installment_date: groupBookingResult.installment_date,
                    total_payment: groupBookingResult.total_payment,
                    bank_transfer_description: groupBookingResult.payment_data,
                    installment_required: groupBookingResult.installment_required,
                }
                this.$data.paymentData = paymentData;
                this.communication.sendMessage(new SetPaymentData(this.communication.getComponentId(),
                    paymentData));
                this.$data.groupBookingResult = groupBookingResult;
                this.communication.sendMessage(new SetGroupBookingResult(this.communication.getComponentId(),
                    groupBookingResult));
                return groupBookingResult;
            }
        });
    }

    sendBookReservationRequest() {
        if(!this.$data.groupReservationBasket?.reservations?.length) {
            this.showError(getTranslation('Błąd'), getTranslation('Brak rezerwacji w koszyku'));
            throw new Error('No reservations in group reservation basket');
        }
        if ((this.$data.groupReservationBasket?.reservations?.length || 0) == 1) {
            return this.sendSingleReservationRequest();
        } else {
            return this.sendGroupReservationRequest();
        }
    }

    /**
     * Sprawdzamy czy możemy zabukować rezerwację, i jeśli tak to jazda
     */
    bookReservation() {
        let checkBookingPossible = new CheckBookingPossible(this.communication.getComponentId());
        const bookingPossibleResultObservable = this.communication.getMessageObservable(NfMessageType.CheckBookingPossibleResult);
        var bookingPossibleResultObservableSubscription:Subscription;
        bookingPossibleResultObservableSubscription = bookingPossibleResultObservable.subscribe( (checkResult) => {
            if(checkResult instanceof CheckBookingPossibleResult && checkResult.requestId == checkBookingPossible.requestId) {
                bookingPossibleResultObservableSubscription.unsubscribe();
                if(checkResult.bookingPossible) {
                    //@ts-ignore
                    this.sendBookReservationRequest().then(async (result) => {
                        if(result) {
                            if (WebDeskKioskStore.isInKiosk) {
                                const {url} = await WebDeskKioskStore.config();
                                const searchParams = new URLSearchParams({
                                    "webdesk[token]": result.webdesk.token,
                                    "webdesk[route]": result.webdesk.route,
                                });
                                window.location.href = `${url}?${searchParams.toString()}`;
                            } else {
                                window.location.href = result.webdesk.url;
                            }
                        } else {
                            this.navigate(NavigationPages.confirmationPage, this.$data.currentPageData as PageData);
                            this.showError(getTranslation('Błąd podczas tworzenia rezerwacji'), getTranslation('Nie udało się zarezerwować rezerwacji'));
                        }
                    }, error => {
                        console.error(error);
                        this.navigate(NavigationPages.confirmationPage, this.$data.currentPageData as PageData);
                        this.showError(getTranslation('Błąd podczas tworzenia rezerwacji'), error);
                    });
                }
            }

        });
        this.communication.sendMessage(checkBookingPossible);

    }
    /**
     * Pokazuje okienko błędu
     * @param title tytuł okienka - tekst musi być podany przetłumaczony
     * @param text tekst w okienku - tekst musi być podany przetłumaczony
     */
    showError(title:translatedString,text:translatedString) {
        this.openModal({
            data: {
                title: title,
                text: text
            },
            templateName: 'errorWindow',
            position: 'screenCenter'
        });
    }
    provide() {
        let provideData: any = {
            widgetId: computed(() => this.$data.widgetId),
            serviceInstanceId: computed(() => this.$data.serviceInstanceId),
            openModal: this.openModal,
            closeModal: this.closeModal,
            currentPage: computed(() => this.$data.currentPage),
            offerChosen:computed(() => this.$data.offerChosen),
            packetChosen:computed(() => this.$data.packetChosen),
            standardChosen: computed(() => this.$data.standardChosen),
            bookingChosen: computed(() => this.$data.bookingChosen),
            currentPageData: computed(() => this.$data.currentPageData),
            reservationSearchData:  computed(() => this.$data.reservationSearchData),
            shopChosen: computed(() => this.$data.shopChosen),
            calculation: computed(() => this.$data.calculation),
            customerData: computed(() => this.$data.customerData),
            invoiceData: computed(() => this.$data.invoiceData),
            guestData: computed(() => this.$data.guestData),
            comment: computed(() => this.$data.comment),
            paymentMethod: computed(() => this.$data.paymentMethod),
            bookingResult: computed(() => this.$data.bookingResult),
            findFirstPeriod: computed(() => this.$data.findFirstPeriod),
            groupReservationBasket: computed(() => this.$data.groupReservationBasket),
            groupReservationService: computed(() => this.$data.groupReservationService),
            config: computed(() => this.$data.config),
            translatedConfig: computed(() => this.$data.translatedConfig),
            configService: computed(() => this.$data.configService),
            equipmentDescriptions: computed( () => this.$data.equipmentDescriptions),
            paymentData: computed( () => this.$data.paymentData),
            isInModal: false,
            bookReservation: this.bookReservation,
            destroyBookingEngine: this.destroy,
            instanceConfiguration: computed(() => this.$data.instanceConfiguration),
            isSearchInProgress: computed(() => this.$data.isSearchInProgress),
            isDesktop: () => window.innerWidth >= 768,
            isOnPage: (...pages: string[]) => pages.includes(this.$data.currentPage),
            favoritesService: computed(() => this.$data.favoritesService),
        };
        return provideData;
    }
}
